import { useQuery, useMutation } from '@tanstack/react-query';
import { notificationService } from '@/services/notificationService';
import { NotificationType } from '@/types/api.types';
import { showErrorToast } from '@/lib/api/errorHandler';
import { message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';

// Query Keys
export const notificationKeys = {
    all: ['notifications'] as const,
    lists: () => [...notificationKeys.all, 'list'] as const,
    list: (filters?: { is_read?: boolean; type?: NotificationType; page?: number; limit?: number }) =>
        [...notificationKeys.lists(), filters] as const,
    unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
    detail: (id: string) => [...notificationKeys.all, 'detail', id] as const,
};

/**
 * Get list of notifications
 */
export const useNotifications = (filters?: {
    is_read?: boolean;
    type?: NotificationType;
    page?: number;
    limit?: number;
}) => {
    return useQuery({
        queryKey: notificationKeys.list(filters),
        queryFn: () => notificationService.getNotifications(filters),
        refetchInterval: 30000, // Refetch every 30 seconds for new notifications
    });
};

/**
 * Get unread notification count
 */
export const useUnreadCount = () => {
    return useQuery({
        queryKey: notificationKeys.unreadCount(),
        queryFn: () => notificationService.getUnreadCount(),
        refetchInterval: 15000, // Refetch every 15 seconds
    });
};

/**
 * Get single notification
 */
export const useNotification = (id: string) => {
    return useQuery({
        queryKey: notificationKeys.detail(id),
        queryFn: () => notificationService.getNotification(id),
        enabled: !!id,
    });
};

/**
 * Mark notification as read
 */
export const useMarkNotificationAsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => notificationService.markAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
        },
        onError: (error) => {
            showErrorToast(error);
        },
    });
};

/**
 * Mark all notifications as read
 */
export const useMarkAllAsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => notificationService.markAllAsRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
            message.success('All notifications marked as read');
        },
        onError: (error) => {
            showErrorToast(error);
        },
    });
};

/**
 * Delete notification
 */
export const useDeleteNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => notificationService.deleteNotification(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
            message.success('Notification deleted');
        },
        onError: (error) => {
            showErrorToast(error);
        },
    });
};
